import { supabase } from '../lib/supabase'
import type { CredentialResponse, GoogleIdConfiguration } from '../types/google'
import { AuthService } from './auth'

export class GoogleAuthService {
  private static initialized = false
  private static onAuthSuccessCallback: (() => void) | null = null
  private static onAuthErrorCallback: ((error: Error) => void) | null = null

  // Set callback for successful authentication
  static setAuthSuccessCallback(callback: (() => void) | null) {
    this.onAuthSuccessCallback = callback
  }

  // Set callback for authentication errors
  static setAuthErrorCallback(callback: ((error: Error) => void) | null) {
    this.onAuthErrorCallback = callback
  }
  
  // Initialize Google Identity Services
  static async initialize(clientId: string): Promise<void> {
    console.log('🔵 GoogleAuthService.initialize() called with clientId:', clientId)
    return new Promise((resolve) => {
      const initializeGoogle = () => {
        if (this.initialized) {
          console.log('🟡 Google Identity Services already initialized')
          resolve()
          return
        }

        try {
          console.log('🔵 Calling window.google.accounts.id.initialize()')
          const config: GoogleIdConfiguration = {
            client_id: clientId,
            callback: (response: CredentialResponse) => this.handleCredentialResponse(response),
            context: 'signin' as const,
            ux_mode: 'popup' as const,
            use_fedcm_for_prompt: true,
            cancel_on_tap_outside: true,
            itp_support: true
          }
          console.log('🔵 Google config:', config)
          
          window.google.accounts.id.initialize(config)
          this.initialized = true
          console.log('🟢 Google Identity Services initialized successfully')
          resolve()
        } catch (error) {
          console.error('🔴 Failed to initialize Google Identity Services:', error)
          resolve() // Continue anyway
        }
      }

      // Check if Google library is already loaded
      if (window.google?.accounts?.id) {
        console.log('🟢 Google library already loaded')
        initializeGoogle()
      } else {
        console.log('🟡 Google library not loaded, waiting...')
        // Wait for the library to load
        const checkGoogleLoaded = () => {
          if (window.google?.accounts?.id) {
            console.log('🟢 Google library loaded after waiting')
            initializeGoogle()
          } else {
            console.log('🟡 Still waiting for Google library...')
            setTimeout(checkGoogleLoaded, 100)
          }
        }
        checkGoogleLoaded()
      }
    })
  }

  // Handle the credential response from Google
  private static async handleCredentialResponse(response: CredentialResponse): Promise<void> {
    console.log('🔵 handleCredentialResponse called', response)
    try {
      console.log('🔵 Google credential received:', response.select_by)
      console.log('🔵 Token length:', response.credential?.length)
      
      // First decode the JWT token to get the user's email
      const payload = JSON.parse(atob(response.credential.split('.')[1]))
      const userEmail = payload.email
      console.log('🔵 Google user email:', userEmail)
      
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: response.credential
      })
      
      console.log('🔵 Supabase response:', { data, error })

      if (error) {
        console.error('🔴 Supabase auth error:', error)

        // Handle specific error cases
        if (error.message?.includes('Provider') && error.message?.includes('not enabled')) {
          console.error('🔴 SOLUTION: Google OAuth is not enabled in your Supabase project.')
          console.error('🔧 Fix: Go to Supabase Dashboard → Authentication → Providers → Enable Google')
          console.error('📖 See: SUPABASE_GOOGLE_OAUTH_SETUP.md for detailed setup guide')
          throw error
        }

        // Handle user registration errors from the database trigger
        if (error.message?.includes('Access denied') || error.message?.includes('Database error')) {
          console.log('🔴 User not pre-registered:', userEmail)
          throw new Error(`アクセスが拒否されました：${userEmail} は登録されていません。学校の管理者にアカウント作成を依頼してください。`)
        }

        throw error
      }

      console.log('🟢 Successfully signed in with Google:', data.user?.email)

      // Call success callback if set
      if (this.onAuthSuccessCallback) {
        console.log('🟢 Calling success callback')
        this.onAuthSuccessCallback()
        this.onAuthSuccessCallback = null // Clear callback
      }

      // Check the complete onboarding flow status (Google OAuth users won't need password change)
      const currentUser = await AuthService.getCurrentUser()
      const flowStatus = await AuthService.getOnboardingFlowStatus(currentUser || undefined)

      // Check if there's a returnUrl in the current URL
      const urlParams = new URLSearchParams(window.location.search)
      const returnUrl = urlParams.get('returnUrl')
      const defaultDestination = currentUser && ['teacher', 'admin', 'super_admin'].includes(currentUser.role)
        ? '/teacher'
        : '/videos'
      const destination = returnUrl ? decodeURIComponent(returnUrl) : defaultDestination

      if (flowStatus.needsOnboarding) {
        console.log('🟡 User needs onboarding, redirecting with onboarding flag')
        // Preserve returnUrl if it exists when redirecting for onboarding
        const onboardingUrl = returnUrl
          ? `${destination}${destination.includes('?') ? '&' : '?'}onboarding=true`
          : `${defaultDestination}?onboarding=true`
        window.location.href = onboardingUrl
      } else {
        console.log('🟢 User completed onboarding, redirecting to:', destination)
        window.location.href = destination
      }
    } catch (error) {
      console.error('🔴 Error signing in with Google:', error)

      // Call error callback if set
      if (this.onAuthErrorCallback && error instanceof Error) {
        console.log('🔴 Calling error callback')
        this.onAuthErrorCallback(error)
      }

      throw error
    }
  }

  // Show Google One Tap prompt
  static async showOneTap(clientId: string): Promise<void> {
    console.log('🔵 GoogleAuthService.showOneTap() called with clientId:', clientId)
    try {
      await this.initialize(clientId)
      
      // Check if user is already signed in
      console.log('🔵 Checking if user is already signed in...')
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        console.log('🟡 User already signed in, skipping One Tap')
        return
      }
      console.log('🔵 User not signed in, showing One Tap prompt')

      window.google.accounts.id.prompt((notification) => {
        console.log('🔵 One Tap notification received:', notification)
        if (notification.isNotDisplayed()) {
          console.log('🟡 One Tap not displayed, reason:', notification.getNotDisplayedReason())
        } else if (notification.isSkippedMoment()) {
          console.log('🟡 One Tap skipped, reason:', notification.getSkippedReason())
        } else if (notification.isDisplayed()) {
          console.log('🟢 One Tap displayed successfully')
        } else if (notification.isDismissedMoment()) {
          console.log('🟡 One Tap dismissed, reason:', notification.getDismissedReason())
        }
      })
      console.log('🟢 One Tap prompt initiated')
    } catch (error) {
      console.error('🔴 Error showing One Tap:', error)
    }
  }

  // Render Google Sign-In button
  static async renderSignInButton(
    container: HTMLElement, 
    clientId: string,
    options: {
      theme?: 'outline' | 'filled_blue' | 'filled_black'
      size?: 'large' | 'medium' | 'small'
      type?: 'standard' | 'icon'
      shape?: 'rectangular' | 'pill'
      text?: 'signin_with' | 'continue_with'
      width?: number
    } = {}
  ): Promise<void> {
    try {
      console.log('🔵 Rendering Google Sign-In button with options:', options)
      await this.initialize(clientId)
      
      // Clear any existing content
      container.innerHTML = ''

      console.log('🔵 Calling renderButton with container:', container)
      window.google.accounts.id.renderButton(container, {
        theme: options.theme || 'outline',
        size: options.size || 'large',
        type: options.type || 'standard',
        shape: options.shape || 'rectangular',
        text: options.text || 'signin_with',
        width: options.width,
        logo_alignment: 'left'
      })
      console.log('🟢 Google Sign-In button rendered successfully')
    } catch (error) {
      console.error('🔴 Error rendering Google Sign-In button:', error)
      throw error
    }
  }

  // Sign out and disable auto-select
  static async signOut(): Promise<void> {
    try {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.disableAutoSelect()
      }
      
      await supabase.auth.signOut()
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    }
  }

  // Generate cryptographic nonce (for enhanced security)
  static async generateNonce(): Promise<{ nonce: string; hashedNonce: string }> {
    const nonce = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))))
    const encoder = new TextEncoder()
    const encodedNonce = encoder.encode(nonce)
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', encodedNonce)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashedNonce = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
    
    return { nonce, hashedNonce }
  }

  // Initialize with nonce for enhanced security
  static async initializeWithNonce(clientId: string): Promise<void> {
    try {
      const { hashedNonce } = await this.generateNonce()
      
      const config: GoogleIdConfiguration = {
        client_id: clientId,
        callback: this.handleCredentialResponse,
        context: 'signin',
        ux_mode: 'popup',
        use_fedcm_for_prompt: true,
        cancel_on_tap_outside: true,
        itp_support: true,
        nonce: hashedNonce
      }

      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize(config)
        this.initialized = true
      }
    } catch (error) {
      console.error('Error initializing with nonce:', error)
      // Fall back to regular initialization
      await this.initialize(clientId)
    }
  }
}